// `object.style.update` op の style パッチをオブジェクトへ反映する共有ロジック。
//
// サーバ権威の op 適用（apps/api の op-applier）とクライアントの楽観適用（apps/web の
// canvas-doc-store）は、同じ入力から同じオブジェクトを導出しなければならない。
// 導出結果が食い違うと、op を送った直後だけ見た目や選択枠がずれ、サーバ確定で直る
// （＝原因を追いにくい）挙動になるため、実装をこの 1 か所へ集約する。

import { computePenStrokeFrame } from "./canvas-bounds";
import type { CanvasObject } from "./canvas-object";
import type { UpdateObjectStyleOp } from "./canvas-op";
import { TEXT_INLINE_STYLE_KEYS, type TextInlineStyleKey } from "./text-inline-style";
import { clearTextInlineOverridesForBaseKeys } from "./text-style-op";

/**
 * `object.style.update` の style パッチを、対応するフィールドを持つオブジェクト種別にだけ反映する。
 * 対象外（image / video / link-card など）のオブジェクトは無視して null を返す。
 *
 * テキストでは、部分書式の削除を伴うのに `textStyleRevision` の採番余地が尽きている場合も
 * null を返す（{@link clearTextInlineOverridesForBaseKeys}）。どちらも「この patch はこの
 * オブジェクトへ適用できない」という同じ意味だが、テキストで null になるのは後者だけなので、
 * テキスト専用の呼び出し側は null を上限到達として利用者へ拒否できる。
 *
 * style の形は op 型（{@link UpdateObjectStyleOp}）を正本とし、ここで再宣言しない。
 * こうしておくと、op へキーを追加したときの追従漏れが型エラーとして表面化する。
 */
export function mergeObjectStyle(
  current: CanvasObject,
  style: UpdateObjectStyleOp["style"]
): CanvasObject | null {
  switch (current.type) {
    case "sticky": {
      // 付箋は color と textAlign を更新しうる。どちらの指定も無ければ現状維持。
      if (style.color === undefined && style.textAlign === undefined) return current;
      const next = { ...current };
      if (style.color !== undefined) next.color = style.color;
      if (style.textAlign !== undefined) next.textAlign = style.textAlign;
      return next;
    }
    case "table": {
      // 表は文字色と文字サイズだけを変更できる。罫線・ヘッダ配色はデザインシステム固定。
      if (style.color === undefined && style.fontSize === undefined) return current;
      const next = { ...current };
      if (style.color !== undefined) next.color = style.color;
      if (style.fontSize !== undefined) next.fontSize = style.fontSize;
      return next;
    }
    case "text": {
      // テキストは color / fill / fontSize / textAlign を更新しうる。いずれの指定も無ければ現状維持。
      if (
        style.color === undefined &&
        style.fill === undefined &&
        style.fontSize === undefined &&
        style.textAlign === undefined &&
        style.bold === undefined
      ) {
        return current;
      }
      const next = { ...current };
      if (style.color !== undefined) next.color = style.color;
      if (style.fill !== undefined) next.fill = style.fill;
      if (style.fontSize !== undefined) next.fontSize = style.fontSize;
      if (style.bold !== undefined) next.bold = style.bold;
      if (style.textAlign !== undefined) next.textAlign = style.textAlign;
      // 全文書式を明示的に変更した項目は、同名の部分書式（inline override）を全 run から削除して
      // 全文へ統一する。旧クライアントの `object.style.update` でも新 op と同じ意味になるよう、
      // 共有関数へ集約する。文字寄せは部分書式を持たないため対象外。
      return clearTextInlineOverridesForBaseKeys(next, presentTextStyleKeys(style));
    }
    case "shape": {
      // 図形は fill / stroke / strokePattern / strokeWidth を更新しうる。
      if (
        style.fill === undefined &&
        style.stroke === undefined &&
        style.strokePattern === undefined &&
        style.strokeWidth === undefined
      ) {
        return current;
      }
      const next = { ...current };
      if (style.fill !== undefined) next.fill = style.fill;
      if (style.stroke !== undefined) next.stroke = style.stroke;
      if (style.strokePattern !== undefined) next.strokePattern = style.strokePattern;
      if (style.strokeWidth !== undefined) next.strokeWidth = style.strokeWidth;
      return next;
    }
    case "connector": {
      // コネクタは stroke / strokeWidth を更新しうる。どちらの指定も無ければ現状維持。
      if (style.stroke === undefined && style.strokeWidth === undefined) return current;
      const next = { ...current };
      if (style.stroke !== undefined) next.stroke = style.stroke;
      if (style.strokeWidth !== undefined) next.strokeWidth = style.strokeWidth;
      return next;
    }
    case "section": {
      // セクションは背景色（fill）のみ変更できる。color / fontSize 等は対象外。
      if (style.fill === undefined) return current;
      return { ...current, fill: style.fill };
    }
    case "pen-stroke": {
      if (
        style.color === undefined &&
        style.thickness === undefined &&
        style.strokePattern === undefined
      ) {
        return current;
      }
      const next = { ...current };
      if (style.color !== undefined) next.color = style.color;
      if (style.strokePattern !== undefined) next.strokePattern = style.strokePattern;
      if (style.thickness !== undefined) {
        next.thickness = style.thickness;
        // 外接矩形は点群と線幅から導出される派生値のため、線幅の変更に追随させる。
        // 追随させないと、選択枠・カリング・範囲選択の判定が実際の描画と一致しなくなる。
        // ペンストロークは選択オーバーレイのハンドルを持たずリサイズ・回転の対象外なので、
        // この再計算が利用者の明示的な指定を上書きすることはない。
        Object.assign(next, computePenStrokeFrame(current.points, style.thickness));
      }
      return next;
    }
    default:
      return null;
  }
}

/** style パッチに含まれる、部分書式と同名の項目キー（文字寄せは含まない）。 */
function presentTextStyleKeys(style: UpdateObjectStyleOp["style"]): readonly TextInlineStyleKey[] {
  return TEXT_INLINE_STYLE_KEYS.filter((key) => style[key] !== undefined);
}
